import Box from '@mui/material/Box';
import { styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { startCase } from 'lodash-es';
import { useContext } from 'react';

import { LoadingSpinner } from '~components/Order/ordersDispatchStyledComponents';
import { Order } from '~hooks/useOrders/models';
import { TicketsRollupSummary } from '~hooks/useTickets/models';
import { numberToIntegerOrDecimal } from '~utils/utilFunctions';

import TicketSummaryQueryContext from './TicketSummaryQueryContext/TicketSummaryQueryContext';

const SummaryBox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.semantic.surfaceSunken,
  border: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
}));

const SummaryRow = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  gap: 10,
  alignItems: 'center',
});

const SummaryItem = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});

const Value = styled(Typography)<{ $color?: string }>(({ theme, $color }) => ({
  fontSize: '0.875rem',
  fontWeight: 600,
  color: $color || theme.palette.text.primary,
  marginLeft: theme.spacing(0.5),
}));

const EMPTY_SUMMARY_DATA = {
  totalDelivered: 0,
  matchedDelivered: 0,
  unmatchedDelivered: 0,
  totalCount: 0,
  matchedCount: 0,
  unmatchedCount: 0,
  unitOfMeasure: '',
};

interface OrderTicketSummaryProps {
  order: Order;
}

const OrderTicketSummary = ({ order }: OrderTicketSummaryProps) => {
  const theme = useTheme();
  const { data: summary, isLoading } = useContext(TicketSummaryQueryContext);

  const orderUnitOfMeasure = startCase(order.unitOfMeasure?.name);

  // Aggregate summary data into the shape of mockdata
  const aggregatedData = summary
    ? summary.reduce(
        (acc: typeof EMPTY_SUMMARY_DATA, item: TicketsRollupSummary) => ({
          totalDelivered: acc.totalDelivered + item.totalTicketsQuantity,
          matchedDelivered: acc.matchedDelivered + item.totalTicketsWithJobQuantity,
          unmatchedDelivered:
            acc.unmatchedDelivered + item.totalTicketsMissingJobQuantity,
          totalCount: acc.totalCount + item.totalTicketsCount,
          matchedCount: acc.matchedCount + item.totalTicketsWithJobCount,
          unmatchedCount: acc.unmatchedCount + item.totalTicketsMissingJobCount,
          unitOfMeasure: item.unitOfMeasure || acc.unitOfMeasure || orderUnitOfMeasure,
        }),
        EMPTY_SUMMARY_DATA,
      )
    : EMPTY_SUMMARY_DATA;

  if (isLoading) {
    return <LoadingSpinner isVisible={isLoading} />;
  }

  return (
    <SummaryBox>
      <SummaryRow>
        <SummaryItem>
          <Typography
            variant="body2"
            sx={{
              fontSize: 14,
              fontWeight: 400,
            }}
          >
            {t('dispatch.tickets_table.summary.total_delivered')}:
          </Typography>
          <Value>
            {numberToIntegerOrDecimal(aggregatedData.totalDelivered)}{' '}
            {startCase(aggregatedData.unitOfMeasure)}
          </Value>
        </SummaryItem>

        <SummaryItem>
          <Typography
            variant="body2"
            sx={{
              fontSize: 14,
              fontWeight: 400,
            }}
          >
            {t('dispatch.tickets_table.summary.total_delivered')}:
          </Typography>
          <Value $color={theme.palette.success.main}>
            {numberToIntegerOrDecimal(aggregatedData.matchedDelivered)}{' '}
            {startCase(aggregatedData.unitOfMeasure)}
          </Value>
        </SummaryItem>

        <SummaryItem>
          <Typography
            variant="body2"
            sx={{
              fontSize: 14,
              fontWeight: 400,
            }}
          >
            {t('dispatch.tickets_table.summary.total')}:
          </Typography>
          <Value $color={theme.palette.error.main}>
            {numberToIntegerOrDecimal(aggregatedData.unmatchedDelivered)}{' '}
            {startCase(aggregatedData.unitOfMeasure)}
          </Value>
        </SummaryItem>
      </SummaryRow>
      <SummaryRow>
        <SummaryItem>
          <Typography
            variant="body2"
            sx={{
              fontSize: 14,
              fontWeight: 400,
            }}
          >
            {t('dispatch.tickets_table.summary.total')}:
          </Typography>
          <Value>{aggregatedData.totalCount}</Value>
        </SummaryItem>

        <SummaryItem>
          <Typography
            variant="body2"
            sx={{
              fontSize: 14,
              fontWeight: 400,
            }}
          >
            {t('dispatch.tickets_table.summary.matched')}:
          </Typography>
          <Value $color={theme.palette.success.main}>{aggregatedData.matchedCount}</Value>
        </SummaryItem>

        <SummaryItem>
          <Typography
            variant="body2"
            sx={{
              fontSize: 14,
              fontWeight: 400,
            }}
          >
            {t('dispatch.tickets_table.summary.not_matched')}:
          </Typography>
          <Value $color={theme.palette.error.main}>{aggregatedData.unmatchedCount}</Value>
        </SummaryItem>
      </SummaryRow>
    </SummaryBox>
  );
};

export default OrderTicketSummary;
